import { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { Text, Button } from '@tremor/react';

// One label found on a sentence. Mirrors the shape inside harmfulResults[].labels.
export type ResultLabel = {
  classifierName?: string;
  label: string;
  backend?: string; // 'openai' | 'phi'
};

type SentenceBbox = {
  sentenceIndex: number;
  bbox?: { page: number };
};

type SentenceProvenance = {
  sentenceIndex: number;
  page?: number | null;
  lineStart?: number | null;
  lineEnd?: number | null;
  speaker?: string | null;
};

interface ResultsTextPanelProps {
  /** Flat list of normalized sentences — index IS the global sentenceIndex used by harmfulResults. */
  sentences: string[];
  /** Optional per-sentence page numbers (PDFs); used to interleave page headers. */
  sentenceBboxes?: SentenceBbox[];
  /** Optional transcript citations (legal transcripts). */
  sentenceProvenance?: SentenceProvenance[];
  /** sentenceIndex -> labels found on that sentence. Presence == flagged. */
  harmfulSentenceMap: Map<number, ResultLabel[]>;
  /** Currently focused flagged sentence (the active match), or null. */
  activeSentenceIndex: number | null;
  onActiveChange: (sentenceIndex: number | null) => void;
}

// Composite key matching the rest of the page (Labels Found, CSV, folder mapping).
function labelKey(l: ResultLabel): string {
  return l.classifierName ? `${l.classifierName}:${l.label}` : l.label;
}

/**
 * Full extracted text with label-bearing sentences highlighted, plus Prev/Next
 * navigation between flagged sentences and a per-label highlight filter. Renders
 * from the flat `sentences` array so each row's index matches
 * harmfulResults[].sentenceIndex exactly (the paged Extracted Text view numbers
 * per-page, which would NOT line up).
 */
export function ResultsTextPanel({
  sentences,
  sentenceBboxes,
  sentenceProvenance,
  harmfulSentenceMap,
  activeSentenceIndex,
  onActiveChange,
}: ResultsTextPanelProps) {
  const sentenceRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // sentenceIndex -> 1-indexed page (same logic as groupSentencesByLabel)
  const pageMap = useMemo(() => {
    const m = new Map<number, number>();
    if (sentenceBboxes) {
      for (const b of sentenceBboxes) {
        if (b.sentenceIndex !== undefined && b.bbox?.page !== undefined) {
          m.set(b.sentenceIndex, b.bbox.page);
        }
      }
    }
    return m;
  }, [sentenceBboxes]);

  const provByIndex = useMemo(() => {
    if (!sentenceProvenance) return null;
    return new Map(sentenceProvenance.map((p) => [p.sentenceIndex, p]));
  }, [sentenceProvenance]);

  // Distinct labels present in this document + how many sentences each flags.
  const { availableLabels, labelCounts } = useMemo(() => {
    const counts = new Map<string, number>();
    for (const labels of harmfulSentenceMap.values()) {
      const seen = new Set<string>();
      for (const l of labels) {
        const k = labelKey(l);
        if (!seen.has(k)) {
          seen.add(k);
          counts.set(k, (counts.get(k) ?? 0) + 1);
        }
      }
    }
    return { availableLabels: Array.from(counts.keys()).sort(), labelCounts: counts };
  }, [harmfulSentenceMap]);

  // Which labels are highlighted. Defaults to all; reset when the document changes.
  const [selectedLabels, setSelectedLabels] = useState<Set<string>>(() => new Set(availableLabels));
  const availKey = availableLabels.join('|');
  useEffect(() => {
    setSelectedLabels(new Set(availKey ? availKey.split('|') : []));
  }, [availKey]);

  // Apply the label filter: a sentence stays flagged if it keeps ≥1 selected label,
  // and only the selected labels' badges show. Drives highlight + Next/Prev.
  const { filteredMap, filteredIndices } = useMemo(() => {
    const map = new Map<number, ResultLabel[]>();
    for (const [idx, labels] of harmfulSentenceMap) {
      const kept = labels.filter((l) => selectedLabels.has(labelKey(l)));
      if (kept.length > 0) map.set(idx, kept);
    }
    return { filteredMap: map, filteredIndices: Array.from(map.keys()).sort((a, b) => a - b) };
  }, [harmfulSentenceMap, selectedLabels]);

  // Scroll the active match into view whenever it changes.
  useEffect(() => {
    if (activeSentenceIndex == null) return;
    const node = sentenceRefs.current.get(activeSentenceIndex);
    node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeSentenceIndex]);

  const goToOffset = useCallback(
    (delta: number) => {
      if (filteredIndices.length === 0) return;
      const current = activeSentenceIndex != null ? filteredIndices.indexOf(activeSentenceIndex) : -1;
      let next: number;
      if (current === -1) {
        next = delta > 0 ? 0 : filteredIndices.length - 1;
      } else {
        next = Math.min(filteredIndices.length - 1, Math.max(0, current + delta));
      }
      onActiveChange(filteredIndices[next]);
    },
    [filteredIndices, activeSentenceIndex, onActiveChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (filteredIndices.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'n' || e.key === 'j') {
      e.preventDefault();
      goToOffset(1);
    } else if (e.key === 'ArrowUp' || e.key === 'p' || e.key === 'k') {
      e.preventDefault();
      goToOffset(-1);
    }
  };

  const hasMatches = filteredIndices.length > 0;
  const position = activeSentenceIndex != null ? filteredIndices.indexOf(activeSentenceIndex) : -1;

  // Build rows imperatively so page headers can be interleaved as the page changes.
  const rows: React.ReactNode[] = [];
  let lastPage: number | undefined;
  sentences.forEach((sentence, i) => {
    const page = pageMap.get(i);
    if (page != null && page !== lastPage) {
      lastPage = page;
      rows.push(
        <div
          key={`page-${page}-${i}`}
          className="sticky top-0 bg-gray-900 py-1 z-10 mt-3 first:mt-0"
        >
          <Text className="text-gray-300 font-medium text-sm">Page {page}</Text>
        </div>
      );
    }

    const labels = filteredMap.get(i);
    const isFlagged = !!labels;
    const isActive = i === activeSentenceIndex;
    const prov = provByIndex?.get(i);
    const cite =
      prov && prov.page != null
        ? `p${prov.page}:${prov.lineStart ?? '?'}${
            prov.lineEnd != null && prov.lineEnd !== prov.lineStart ? `-${prov.lineEnd}` : ''
          }`
        : null;

    rows.push(
      <div
        key={i}
        ref={(el) => {
          if (el) sentenceRefs.current.set(i, el);
          else sentenceRefs.current.delete(i);
        }}
        onClick={isFlagged ? () => onActiveChange(i) : undefined}
        className={`flex gap-2 rounded px-1 py-0.5 transition-colors ${
          isActive
            ? 'bg-amber-500/15 ring-1 ring-amber-500 border-l-2 border-amber-400 cursor-pointer'
            : isFlagged
            ? 'bg-red-950/40 border-l-2 border-red-700/60 cursor-pointer hover:bg-red-950/60'
            : ''
        }`}
      >
        <Text className="text-gray-600 text-xs font-mono shrink-0 pt-0.5 select-none w-8 text-right">
          {i + 1}
        </Text>
        {cite && (
          <Text
            className="text-violet-400/70 text-xs font-mono shrink-0 pt-0.5 select-none whitespace-nowrap"
            title={prov?.speaker ? `speaker: ${prov.speaker}` : undefined}
          >
            {cite}
            {prov?.speaker ? ` ${prov.speaker}` : ''}
          </Text>
        )}
        <div className="min-w-0">
          <Text
            className={`text-sm leading-relaxed break-words ${
              isFlagged ? 'text-gray-200' : 'text-gray-400'
            }`}
          >
            {sentence}
          </Text>
          {isFlagged && labels && (
            <div className="flex flex-wrap gap-1 mt-1">
              {labels.map((l, li) => (
                <span
                  key={li}
                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium bg-red-900/40 text-red-300"
                >
                  {l.backend && (
                    <span
                      className={`px-1 rounded ${
                        l.backend === 'phi'
                          ? 'bg-purple-900/60 text-purple-300'
                          : 'bg-blue-900/60 text-blue-300'
                      }`}
                    >
                      {l.backend === 'phi' ? 'Phi' : 'OpenAI'}
                    </span>
                  )}
                  {labelKey(l)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  });

  return (
    <div tabIndex={0} onKeyDown={handleKeyDown} className="outline-none">
      {/* Navigation toolbar — stays visible above the scrolling list */}
      <div className="flex items-center justify-between gap-2 pb-3 mb-2 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <Text className="text-gray-400 text-sm whitespace-nowrap">
            {hasMatches
              ? `Match ${position >= 0 ? position + 1 : '–'} of ${filteredIndices.length}`
              : 'No labels found'}
          </Text>
          {availableLabels.length > 0 && (
            <LabelFilterDropdown
              availableLabels={availableLabels}
              labelCounts={labelCounts}
              selected={selectedLabels}
              onChange={setSelectedLabels}
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="xs" variant="secondary" disabled={!hasMatches} onClick={() => goToOffset(-1)}>
            ↑ Prev
          </Button>
          <Button size="xs" variant="secondary" disabled={!hasMatches} onClick={() => goToOffset(1)}>
            Next ↓
          </Button>
        </div>
      </div>

      <div className="space-y-1 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">{rows}</div>
    </div>
  );
}

/** Multi-select checkbox dropdown choosing which labels are highlighted. */
function LabelFilterDropdown({
  availableLabels,
  labelCounts,
  selected,
  onChange,
}: {
  availableLabels: string[];
  labelCounts: Map<string, number>;
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const allSelected = selected.size === availableLabels.length;
  const noneSelected = selected.size === 0;
  const summary = allSelected ? 'All labels' : noneSelected ? 'None' : `${selected.size} of ${availableLabels.length}`;

  const toggle = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
        title="Choose which labels are highlighted"
      >
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M7 12h10M11 20h2" />
        </svg>
        <span className="whitespace-nowrap">Highlight: {summary}</span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 mt-1 w-72 max-h-80 overflow-y-auto rounded-md border border-gray-700 bg-gray-900 shadow-xl z-30 p-2">
          <div className="flex items-center justify-between px-1 pb-2 mb-1 border-b border-gray-800">
            <button
              type="button"
              onClick={() => onChange(new Set(availableLabels))}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => onChange(new Set())}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Select none
            </button>
          </div>
          <div className="space-y-0.5">
            {availableLabels.map((key) => (
              <label
                key={key}
                className="flex items-center gap-2 px-1 py-1 rounded hover:bg-gray-800 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.has(key)}
                  onChange={() => toggle(key)}
                  className="accent-blue-600 shrink-0"
                />
                <span className="text-sm text-gray-300 flex-1 truncate" title={key}>
                  {key}
                </span>
                <span className="text-xs text-gray-500 shrink-0">{labelCounts.get(key) ?? 0}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
